import React, { useEffect, useMemo, useState } from 'react';
import AlternateEmailOutlinedIcon from '@mui/icons-material/AlternateEmailOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';

import Axios from '../../../networking/intraceptor';
import { useCustomDispatch } from '../../../hooks';
import { useCustomSelectors } from '../../../services/selectors/allSelectors';
import { loginSuccess } from '../../../services/actions/auth/authActions';
import { fetchPharmacySettingsList } from '../../../services/actions/admin/team/fetchTeamsList';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

const createRow = (value = '') => ({
	id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
	value,
});

const buildRows = (emails = []) => {
	const rows = (emails || []).map((email) => createRow(email));
	return rows.length > 0 ? rows : [createRow()];
};

const normalizeTimeValue = (value = '') => {
	if (!value) {
		return '';
	}

	return String(value).slice(0, 5);
};

const normalizeRows = (rows = []) =>
	rows
		.map((row) => ({
			...row,
			value: row.value.trim(),
		}))
		.filter((row) => row.value !== '');

const validateEmailRows = (rows = [], emptyMessage) => {
	const normalizedRows = normalizeRows(rows);
	const rowErrors = {};
	const seenEmails = new Map();

	if (normalizedRows.length === 0) {
		return {
			values: [],
			rowErrors,
			sectionError: emptyMessage,
		};
	}

	normalizedRows.forEach((row) => {
		const normalizedEmail = row.value.toLowerCase();

		if (!EMAIL_PATTERN.test(row.value)) {
			rowErrors[row.id] = 'Enter a valid email address.';
			return;
		}

		if (seenEmails.has(normalizedEmail)) {
			rowErrors[row.id] = 'Duplicate email address.';
			rowErrors[seenEmails.get(normalizedEmail)] = 'Duplicate email address.';
			return;
		}

		seenEmails.set(normalizedEmail, row.id);
	});

	return {
		values: normalizedRows.map((row) => row.value),
		rowErrors,
		sectionError: Object.keys(rowErrors).length > 0 ? 'Fix the highlighted email addresses.' : '',
	};
};

const EmailSection = ({
	title,
	timeValue,
	timeError,
	onTimeChange,
	rows,
	rowErrors,
	sectionError,
	onChange,
	onAdd,
	onRemove,
	disabled,
}) => (
	<div className="rounded-2xl border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur-sm">
		<div className="mb-4">
			<h3 className="text-base font-semibold text-slate-900">{title}</h3>
		</div>

		<div className="mb-4">
			<input
				type="time"
				value={timeValue}
				onChange={(event) => onTimeChange(event.target.value)}
				disabled={disabled}
				className={`h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition ${
					timeError
						? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
						: 'border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
				} ${disabled ? 'cursor-not-allowed bg-slate-100' : ''}`}
			/>
			{timeError && <p className="mt-2 text-sm text-red-600">{timeError}</p>}
		</div>

		<div className="space-y-3">
			{rows.map((row, index) => (
				<div key={row.id}>
					<div className="flex items-start gap-3">
						<div className="flex-1">
							<input
								type="email"
								value={row.value}
								onChange={(event) => onChange(row.id, event.target.value)}
								disabled={disabled}
								placeholder={`Email ${index + 1}`}
								className={`h-12 w-full rounded-2xl border bg-white px-4 text-sm text-slate-900 outline-none transition ${
									rowErrors[row.id]
										? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
										: 'border-gray-300 focus:border-blue-600 focus:ring-4 focus:ring-blue-100'
								} ${disabled ? 'cursor-not-allowed bg-slate-100' : ''}`}
							/>
							{rowErrors[row.id] && (
								<p className="mt-1 text-xs text-red-600">{rowErrors[row.id]}</p>
							)}
						</div>
						<button
							type="button"
							onClick={() => onRemove(row.id)}
							disabled={disabled}
							aria-label={`Remove email ${index + 1}`}
							title="Remove email"
							className="flex h-12 w-12 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
						>
							<DeleteOutlineOutlinedIcon fontSize="small" />
						</button>
					</div>
				</div>
			))}
		</div>

		<div className="mt-4 flex items-center justify-between gap-3">
			<button
				type="button"
				onClick={onAdd}
				disabled={disabled}
				className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
			>
				Add email
			</button>
		</div>
		{sectionError && <p className="mt-3 text-sm text-red-600">{sectionError}</p>}
	</div>
);

const RequiredDeliveryEmailSetupModal = () => {
	const dispatch = useCustomDispatch();
	const { authData, adminModule, pharmacyId, pharmacyLoginData } = useCustomSelectors();

	const requiredSetup = pharmacyLoginData?.required_delivery_email_setup;
	const shouldPrompt = Number(adminModule) === 1 && Boolean(requiredSetup?.requires_prompt);

	const setupKey = useMemo(() => {
		if (!pharmacyId) {
			return null;
		}

		return `${pharmacyId}:${shouldPrompt ? 'required' : 'ready'}`;
	}, [pharmacyId, shouldPrompt]);

	const [hasResolvedCurrentPrompt, setHasResolvedCurrentPrompt] = useState(false);
	const [initializedSetupKey, setInitializedSetupKey] = useState(null);
	const [isOpen, setIsOpen] = useState(false);
	const [activeRequest, setActiveRequest] = useState('');
	const [submitError, setSubmitError] = useState('');
	const [mismatchTime, setMismatchTime] = useState('');
	const [deliveryAccessTime, setDeliveryAccessTime] = useState('');
	const [mismatchRows, setMismatchRows] = useState([createRow()]);
	const [deliveryAccessRows, setDeliveryAccessRows] = useState([createRow()]);
	const [fieldErrors, setFieldErrors] = useState({
		mismatchTime: '',
		deliveryTime: '',
		mismatch: {},
		delivery: {},
		mismatchSection: '',
		deliverySection: '',
	});

	useEffect(() => {
		setHasResolvedCurrentPrompt(false);
		setInitializedSetupKey(null);
		setIsOpen(false);
		setSubmitError('');
		setMismatchTime('');
		setDeliveryAccessTime('');
		setFieldErrors({
			mismatchTime: '',
			deliveryTime: '',
			mismatch: {},
			delivery: {},
			mismatchSection: '',
			deliverySection: '',
		});
	}, [pharmacyId]);

	useEffect(() => {
		if (!shouldPrompt || hasResolvedCurrentPrompt || !setupKey) {
			setIsOpen(false);
			return;
		}

		if (initializedSetupKey === setupKey) {
			return;
		}

		setMismatchRows(buildRows(requiredSetup?.mismatch_delivery_emails));
		setDeliveryAccessRows(buildRows(requiredSetup?.delivery_access_code_emails));
		setMismatchTime(normalizeTimeValue(requiredSetup?.mismatch_delivery_email_time));
		setDeliveryAccessTime(normalizeTimeValue(requiredSetup?.delivery_access_code_email_time));
		setFieldErrors({
			mismatchTime: '',
			deliveryTime: '',
			mismatch: {},
			delivery: {},
			mismatchSection: '',
			deliverySection: '',
		});
		setSubmitError('');
		setIsOpen(true);
		setInitializedSetupKey(setupKey);
	}, [
		hasResolvedCurrentPrompt,
		initializedSetupKey,
		requiredSetup?.delivery_access_code_email_time,
		requiredSetup?.delivery_access_code_emails,
		requiredSetup?.mismatch_delivery_email_time,
		requiredSetup?.mismatch_delivery_emails,
		setupKey,
		shouldPrompt,
	]);

	useEffect(() => {
		if (!isOpen) {
			return undefined;
		}

		const { overflow } = document.body.style;
		document.body.style.overflow = 'hidden';

		return () => {
			document.body.style.overflow = overflow;
		};
	}, [isOpen]);

	const updateRows = (rows, rowId, nextValue) =>
		rows.map((row) => (row.id === rowId ? { ...row, value: nextValue } : row));

	const removeRow = (rows, rowId) => {
		if (rows.length === 1) {
			return rows.map((row) => (row.id === rowId ? { ...row, value: '' } : row));
		}

		return rows.filter((row) => row.id !== rowId);
	};

	const handleMismatchChange = (rowId, nextValue) => {
		setMismatchRows((currentRows) => updateRows(currentRows, rowId, nextValue));
		setFieldErrors((currentErrors) => ({
			...currentErrors,
			mismatch: { ...currentErrors.mismatch, [rowId]: '' },
			mismatchSection: '',
		}));
	};

	const handleMismatchTimeChange = (nextValue) => {
		setMismatchTime(nextValue);
		setFieldErrors((currentErrors) => ({
			...currentErrors,
			mismatchTime: '',
		}));
	};

	const handleDeliveryAccessChange = (rowId, nextValue) => {
		setDeliveryAccessRows((currentRows) => updateRows(currentRows, rowId, nextValue));
		setFieldErrors((currentErrors) => ({
			...currentErrors,
			delivery: { ...currentErrors.delivery, [rowId]: '' },
			deliverySection: '',
		}));
	};

	const handleDeliveryAccessTimeChange = (nextValue) => {
		setDeliveryAccessTime(nextValue);
		setFieldErrors((currentErrors) => ({
			...currentErrors,
			deliveryTime: '',
		}));
	};

	const applyRequiredDeliveryEmailSetup = (updatedSetup) => {
		if (!authData?.data) {
			return;
		}

		dispatch(
			loginSuccess({
				...authData.data,
				settings: authData.data.settings
					? {
							...authData.data.settings,
							required_delivery_email_prompt_dismissed: Boolean(
								updatedSetup?.prompt_dismissed
							),
					  }
					: authData.data.settings,
				pharmacy: (authData.data.pharmacy || []).map((pharmacy) =>
					pharmacy.id === pharmacyId
						? {
								...pharmacy,
								required_delivery_email_setup: updatedSetup,
						  }
						: pharmacy
				),
			})
		);
	};

	const handleSubmit = async (event) => {
		event.preventDefault();

		const mismatchValidation = validateEmailRows(
			mismatchRows,
			'Add at least one mismatch delivery email.'
		);
		const deliveryAccessValidation = validateEmailRows(
			deliveryAccessRows,
			'Add at least one delivery access code email.'
		);
		const normalizedMismatchTime = normalizeTimeValue(mismatchTime);
		const normalizedDeliveryAccessTime = normalizeTimeValue(deliveryAccessTime);

		setFieldErrors({
			mismatchTime: normalizedMismatchTime ? '' : 'Select mismatch delivery mail time.',
			deliveryTime: normalizedDeliveryAccessTime ? '' : 'Select delivery access code mail time.',
			mismatch: mismatchValidation.rowErrors,
			delivery: deliveryAccessValidation.rowErrors,
			mismatchSection: mismatchValidation.sectionError,
			deliverySection: deliveryAccessValidation.sectionError,
		});
		setSubmitError('');

		if (
			!normalizedMismatchTime ||
			!normalizedDeliveryAccessTime ||
			mismatchValidation.sectionError ||
			deliveryAccessValidation.sectionError ||
			Object.keys(mismatchValidation.rowErrors).length > 0 ||
			Object.keys(deliveryAccessValidation.rowErrors).length > 0
		) {
			return;
		}

		setActiveRequest('save');

		try {
			const response = await Axios.post('required-delivery-email-setup', {
				mismatch_delivery_email_time: normalizedMismatchTime,
				mismatch_delivery_emails: mismatchValidation.values,
				delivery_access_code_email_time: normalizedDeliveryAccessTime,
				delivery_access_code_emails: deliveryAccessValidation.values,
			});

			const updatedSetup =
				response?.data?.data?.required_delivery_email_setup || {
					requires_prompt: false,
					has_mismatch_delivery_emails: true,
					has_delivery_access_code_emails: true,
					has_mismatch_delivery_email_time: true,
					has_delivery_access_code_email_time: true,
					mismatch_delivery_email_time: normalizedMismatchTime,
					delivery_access_code_email_time: normalizedDeliveryAccessTime,
					mismatch_delivery_emails: mismatchValidation.values,
					delivery_access_code_emails: deliveryAccessValidation.values,
				};

			applyRequiredDeliveryEmailSetup(updatedSetup);

			dispatch(fetchPharmacySettingsList('winrx_settings'));

			setHasResolvedCurrentPrompt(true);
			setIsOpen(false);
		} catch (error) {
			const responseErrors = error?.response?.data?.errors || {};
			const mismatchTimeError =
				responseErrors?.mismatch_delivery_email_time?.[0] || '';
			const deliveryTimeError =
				responseErrors?.delivery_access_code_email_time?.[0] || '';
			const mismatchSectionError =
				responseErrors?.mismatch_delivery_emails?.[0] || responseErrors?.['mismatch_delivery_emails.0']?.[0] || '';
			const deliverySectionError =
				responseErrors?.delivery_access_code_emails?.[0] ||
				responseErrors?.['delivery_access_code_emails.0']?.[0] ||
				'';

			setFieldErrors((currentErrors) => ({
				...currentErrors,
				mismatchTime: mismatchTimeError || currentErrors.mismatchTime,
				deliveryTime: deliveryTimeError || currentErrors.deliveryTime,
				mismatchSection: mismatchSectionError || currentErrors.mismatchSection,
				deliverySection: deliverySectionError || currentErrors.deliverySection,
			}));

			setSubmitError(
				error?.response?.data?.message ||
					'Unable to save the required emails right now. Please try again.'
			);
		} finally {
			setActiveRequest('');
		}
	};

	const handleCancel = async () => {
		setSubmitError('');
		setActiveRequest('cancel');

		try {
			const response = await Axios.post('dismiss-required-delivery-email-setup');
			const updatedSetup =
				response?.data?.data?.required_delivery_email_setup || {
					requires_prompt: false,
					has_mismatch_delivery_emails: Boolean(
						requiredSetup?.has_mismatch_delivery_emails
					),
					has_delivery_access_code_emails: Boolean(
						requiredSetup?.has_delivery_access_code_emails
					),
					has_mismatch_delivery_email_time: Boolean(
						requiredSetup?.has_mismatch_delivery_email_time
					),
					has_delivery_access_code_email_time: Boolean(
						requiredSetup?.has_delivery_access_code_email_time
					),
					prompt_dismissed: true,
					mismatch_delivery_email_time:
						requiredSetup?.mismatch_delivery_email_time || '',
					delivery_access_code_email_time:
						requiredSetup?.delivery_access_code_email_time || '',
					mismatch_delivery_emails: requiredSetup?.mismatch_delivery_emails || [],
					delivery_access_code_emails: requiredSetup?.delivery_access_code_emails || [],
				};

			applyRequiredDeliveryEmailSetup(updatedSetup);
			setHasResolvedCurrentPrompt(true);
			setIsOpen(false);
		} catch (error) {
			setSubmitError(
				error?.response?.data?.message ||
					'Unable to dismiss this popup right now. Please try again.'
			);
		} finally {
			setActiveRequest('');
		}
	};

	const isSubmitting = activeRequest !== '';

	if (!isOpen) {
		return null;
	}

	return (
		<div className="fixed inset-0 z-[155] flex items-center justify-center bg-slate-950/45 px-4 py-8 backdrop-blur-sm">
			<div className="relative w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-[linear-gradient(145deg,#ffffff_0%,#f8fbff_55%,#f8fafc_100%)] p-6 shadow-[0_32px_120px_rgba(15,23,42,0.24)]">
				<div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,0.18),transparent_48%),radial-gradient(circle_at_top_right,rgba(14,165,233,0.12),transparent_36%)]" />
				<form onSubmit={handleSubmit}>
					<div className="relative">
						<div className="flex items-center gap-4">
							<span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-blue-700 ring-1 ring-blue-100">
								<AlternateEmailOutlinedIcon />
							</span>
							<div className="flex flex-1 items-center min-h-12">
								<h2 className="text-2xl font-semibold text-slate-900">Required email setup</h2>
							</div>
						</div>

						<div className="mt-7 grid gap-4 lg:grid-cols-2">
							<EmailSection
								title="Mismatch Delivery Mail"
								timeValue={mismatchTime}
								timeError={fieldErrors.mismatchTime}
								onTimeChange={handleMismatchTimeChange}
								rows={mismatchRows}
								rowErrors={fieldErrors.mismatch}
								sectionError={fieldErrors.mismatchSection}
								onChange={handleMismatchChange}
								onAdd={() => setMismatchRows((currentRows) => [...currentRows, createRow()])}
								onRemove={(rowId) =>
									setMismatchRows((currentRows) => removeRow(currentRows, rowId))
								}
								disabled={isSubmitting}
							/>

							<EmailSection
								title="Delivery Access Code Mail"
								timeValue={deliveryAccessTime}
								timeError={fieldErrors.deliveryTime}
								onTimeChange={handleDeliveryAccessTimeChange}
								rows={deliveryAccessRows}
								rowErrors={fieldErrors.delivery}
								sectionError={fieldErrors.deliverySection}
								onChange={handleDeliveryAccessChange}
								onAdd={() =>
									setDeliveryAccessRows((currentRows) => [...currentRows, createRow()])
								}
								onRemove={(rowId) =>
									setDeliveryAccessRows((currentRows) => removeRow(currentRows, rowId))
								}
								disabled={isSubmitting}
							/>
						</div>

						{submitError && <p className="mt-5 text-sm text-red-600">{submitError}</p>}

						<div className="mt-6 flex items-center justify-end gap-3">
							<button
								type="button"
								onClick={handleCancel}
								disabled={isSubmitting}
								className="rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
							>
								{activeRequest === 'cancel' ? 'Cancelling...' : 'Cancel'}
							</button>
							<button
								type="submit"
								disabled={isSubmitting}
								className={`rounded-xl border px-5 py-3 text-sm font-semibold transition ${
									isSubmitting
										? 'cursor-not-allowed border-slate-300 bg-slate-200 text-slate-600'
										: 'border-blue-600 bg-blue-600 text-white shadow-sm hover:bg-blue-700'
								}`}
							>
								{activeRequest === 'save' ? 'Saving...' : 'Submit'}
							</button>
						</div>
					</div>
				</form>
			</div>
		</div>
	);
};

export default RequiredDeliveryEmailSetupModal;
